import { z } from "zod";
import { Id, IsoTimestamp } from "../primitives.js";

/**
 * Leaving, deleting a household, deleting an account, and taking the data with
 * you — docs/09 §9.7.
 *
 * The three destructive operations are deliberately separate rather than one
 * "delete me" that infers what was meant. Leaving a shared household and
 * destroying its ledger are different intentions with different blast radii,
 * and a dialog is a poor place to discover which one you picked.
 */

/** How long a soft-deleted household is recoverable before the sweeper takes it. */
export const DELETION_GRACE_DAYS = 30;

/**
 * Destructive requests carry the name of the thing being destroyed, and the
 * server checks it. A confirmation that lives only in the browser is one a
 * mistyped `fetch` walks straight past.
 */
export const ConfirmByName = z.object({
  confirm: z.string().min(1, "Type the name to confirm"),
});

export const HouseholdDeleted = z.object({
  householdId: Id,
  name: z.string(),
  deletedAt: IsoTimestamp,
  /** When the sweeper will purge it. Shown so the grace period is a promise, not a footnote. */
  purgeAfter: IsoTimestamp,
});

export const HouseholdLeft = z.object({
  householdId: Id,
  name: z.string(),
  /** What is left to switch to, so the client knows whether to send them to "create one". */
  remainingHouseholds: z.number().int().min(0),
});

/**
 * Why the button is disabled, in a form the client can act on rather than a
 * sentence it has to parse. `last_owner` is the only one a person can fix
 * themselves, and the UI says how.
 */
export const DeletionBlocker = z.enum(["last_owner", "sole_member"]);

export const AccountDeletable = z.object({
  /** False while the person is still in a household — they have to leave or delete it first. */
  deletable: z.boolean(),
  households: z.array(
    z.object({
      householdId: Id,
      name: z.string(),
      role: z.string(),
      /** Absent when they can simply leave. */
      blocker: DeletionBlocker.nullable(),
    }),
  ),
});

export const AccountDeleted = z.object({
  /**
   * The user row is scrubbed rather than dropped where something they created
   * still points at it — see migration 0009. True when nothing did and the row
   * went with it.
   */
  purgedImmediately: z.boolean(),
});

export type ConfirmByName = z.infer<typeof ConfirmByName>;
export type HouseholdDeleted = z.infer<typeof HouseholdDeleted>;
export type HouseholdLeft = z.infer<typeof HouseholdLeft>;
export type DeletionBlocker = z.infer<typeof DeletionBlocker>;
export type AccountDeletable = z.infer<typeof AccountDeletable>;
export type AccountDeleted = z.infer<typeof AccountDeleted>;
